import { useEffect, useRef, useState, useCallback } from 'react';

interface Props {
  currentLocation: { lat: number; lng: number } | null;
  customerLat: number | null;
  customerLng: number | null;
  customerAddress: string;
  locationStatus: 'idle' | 'tracking' | 'error';
  dbName: string;
  activeOrderId: string;
  startLocationTracking: (orderId: string) => void;
  showToast: (msg: string, type?: 'success' | 'error' | 'info') => void;
}

function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t;
}

function calcDistance(lat1: number, lng1: number, lat2: number, lng2: number) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function calcBearing(lat1: number, lng1: number, lat2: number, lng2: number) {
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const y = Math.sin(dLng) * Math.cos((lat2 * Math.PI) / 180);
  const x =
    Math.cos((lat1 * Math.PI) / 180) * Math.sin((lat2 * Math.PI) / 180) -
    Math.sin((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.cos(dLng);
  return ((Math.atan2(y, x) * 180) / Math.PI + 360) % 360;
}

function toXY(
  lat: number,
  lng: number,
  bounds: { minLat: number; maxLat: number; minLng: number; maxLng: number },
  W: number,
  H: number,
  pad: number
) {
  const lr = bounds.maxLat - bounds.minLat || 0.01;
  const lgr = bounds.maxLng - bounds.minLng || 0.01;
  return {
    x: pad + ((lng - bounds.minLng) / lgr) * (W - 2 * pad),
    y: pad + ((bounds.maxLat - lat) / lr) * (H - 2 * pad),
  };
}

const W = 360;
const H = 260;
const PAD = 40;

export default function DeliveryRouteMap({
  currentLocation,
  customerLat,
  customerLng,
  customerAddress,
  locationStatus,
  dbName,
  activeOrderId,
  startLocationTracking,
  showToast,
}: Props) {
  const animRef = useRef<number | null>(null);
  const prevBike = useRef<{ x: number; y: number } | null>(null);
  const targetBike = useRef<{ x: number; y: number } | null>(null);
  const curBike = useRef<{ x: number; y: number } | null>(null);
  const animProg = useRef(1);
  const dotAnimRef = useRef(0);

  const [bikePos, setBikePos] = useState<{ x: number; y: number } | null>(null);
  const [custPos, setCustPos] = useState<{ x: number; y: number } | null>(null);
  const [bearing, setBearing] = useState(0);
  const [dotOffset, setDotOffset] = useState(0);
  const [pulsing, setPulsing] = useState(false);

  const distance =
    currentLocation && customerLat && customerLng
      ? calcDistance(currentLocation.lat, currentLocation.lng, customerLat, customerLng)
      : null;

  const distLabel =
    distance === null
      ? null
      : distance < 1
      ? `${Math.round(distance * 1000)} m`
      : `${distance.toFixed(1)} km`;

  const etaMins = distance !== null ? Math.ceil((distance / 20) * 60) : null;
  const etaLabel =
    etaMins === null ? null : etaMins <= 1 ? 'Almost here!' : etaMins < 60 ? `~${etaMins} min` : `~${Math.ceil(etaMins / 60)} hr`;

  const isClose = distance !== null && distance <= 0.5;
  const isNear = distance !== null && distance > 0.5 && distance <= 2;

  const compute = useCallback(() => {
    if (!customerLat || !customerLng) return;

    const lats = [customerLat];
    const lngs = [customerLng];
    if (currentLocation) {
      lats.push(currentLocation.lat);
      lngs.push(currentLocation.lng);
    }

    const bounds = {
      minLat: Math.min(...lats) - 0.006,
      maxLat: Math.max(...lats) + 0.006,
      minLng: Math.min(...lngs) - 0.006,
      maxLng: Math.max(...lngs) + 0.006,
    };

    const cp = toXY(customerLat, customerLng, bounds, W, H, PAD);
    setCustPos(cp);

    if (currentLocation) {
      const dp = toXY(currentLocation.lat, currentLocation.lng, bounds, W, H, PAD);

      if (targetBike.current) {
        const prev = targetBike.current;
        if (Math.hypot(prev.x - dp.x, prev.y - dp.y) > 0.5) {
          prevBike.current = curBike.current || dp;
          targetBike.current = dp;
          animProg.current = 0;
          setPulsing(true);
          setTimeout(() => setPulsing(false), 900);
        }
      } else {
        prevBike.current = dp;
        targetBike.current = dp;
        curBike.current = dp;
        setBikePos(dp);
      }

      setBearing(calcBearing(currentLocation.lat, currentLocation.lng, customerLat, customerLng));
    }
  }, [currentLocation, customerLat, customerLng]);

  // Animation loop
  useEffect(() => {
    let frame: number;
    const tick = () => {
      // Smooth bike movement
      if (prevBike.current && targetBike.current && animProg.current < 1) {
        animProg.current = Math.min(animProg.current + 0.03, 1);
        const t = animProg.current;
        const e = t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
        const np = {
          x: lerp(prevBike.current.x, targetBike.current.x, e),
          y: lerp(prevBike.current.y, targetBike.current.y, e),
        };
        curBike.current = np;
        setBikePos(np);
      }
      // Animate dot offset for dashed route line
      dotAnimRef.current = (dotAnimRef.current + 0.4) % 20;
      setDotOffset(dotAnimRef.current);
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, []);

  useEffect(() => {
    compute();
  }, [compute]);

  // Road waypoints — mid-point slightly offset to simulate curved road
  const routePath = bikePos && custPos
    ? (() => {
        const mx = (bikePos.x + custPos.x) / 2 + (custPos.y - bikePos.y) * 0.25;
        const my = (bikePos.y + custPos.y) / 2 - (custPos.x - bikePos.x) * 0.25;
        return `M ${bikePos.x} ${bikePos.y} Q ${mx} ${my} ${custPos.x} ${custPos.y}`;
      })()
    : null;

  // Only customer — center it
  const onlyCustomer = !currentLocation && custPos;

  const routeColor = isClose ? '#16a34a' : isNear ? '#d97706' : '#ea580c';

  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className={`px-4 py-2.5 border-b flex items-center justify-between ${
        locationStatus === 'tracking' ? 'bg-green-50 border-green-100' : 'bg-gray-50 border-gray-100'
      }`}>
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-lg bg-gray-900 flex items-center justify-center">
            <i className="ri-e-bike-2-fill text-white" style={{ fontSize: 11 }}></i>
          </div>
          <p className="text-gray-700 font-bold text-xs">ClothyBazar Live Route</p>
        </div>
        {locationStatus === 'tracking' ? (
          <span className="flex items-center gap-1.5 text-xs text-green-600 font-bold bg-green-100 border border-green-200 px-2.5 py-1 rounded-full">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse inline-block"></span>
            LIVE GPS
          </span>
        ) : locationStatus === 'error' ? (
          <span className="flex items-center gap-1.5 text-xs text-red-600 font-bold bg-red-50 border border-red-200 px-2.5 py-1 rounded-full">
            <i className="ri-error-warning-line text-xs"></i>GPS Error
          </span>
        ) : (
          <span className="flex items-center gap-1 text-xs text-amber-500 font-medium">
            <i className="ri-loader-4-line animate-spin text-xs"></i>
            GPS connecting...
          </span>
        )}
      </div>

      {/* SVG Map Canvas */}
      <div
        className="relative overflow-hidden"
        style={{
          height: 280,
          background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 50%, #e2e8f0 100%)',
        }}
      >
        {/* Street grid background */}
        <svg className="absolute inset-0 w-full h-full opacity-30" style={{ pointerEvents: 'none' }}>
          <defs>
            <pattern id="streets" width="50" height="50" patternUnits="userSpaceOnUse">
              <rect width="50" height="50" fill="none" />
              <line x1="50" y1="0" x2="50" y2="50" stroke="#94a3b8" strokeWidth="0.8" />
              <line x1="0" y1="50" x2="50" y2="50" stroke="#94a3b8" strokeWidth="0.8" />
            </pattern>
            <pattern id="blocks" width="50" height="50" patternUnits="userSpaceOnUse">
              <rect x="2" y="2" width="44" height="44" rx="3" fill="#cbd5e1" fillOpacity="0.4" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#blocks)" />
          <rect width="100%" height="100%" fill="url(#streets)" />
        </svg>

        {/* Main SVG */}
        <svg viewBox={`0 0 ${W} ${H}`} width="100%" height="100%" className="relative z-10">
          <defs>
            <radialGradient id="bikeGlow2" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#f97316" stopOpacity="0.5" />
              <stop offset="100%" stopColor="#f97316" stopOpacity="0" />
            </radialGradient>
            <radialGradient id="custGlow2" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#16a34a" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#16a34a" stopOpacity="0" />
            </radialGradient>
            <filter id="shadow2" x="-20%" y="-20%" width="140%" height="140%">
              <feDropShadow dx="0" dy="2" stdDeviation="3" floodColor="#00000030" />
            </filter>
          </defs>

          {/* Route road shadow */}
          {routePath && (
            <path
              d={routePath}
              fill="none"
              stroke="#00000015"
              strokeWidth="10"
              strokeLinecap="round"
            />
          )}

          {/* Road base — white road */}
          {routePath && (
            <path
              d={routePath}
              fill="none"
              stroke="white"
              strokeWidth="7"
              strokeLinecap="round"
            />
          )}

          {/* Route center dashed line */}
          {routePath && (
            <path
              d={routePath}
              fill="none"
              stroke={routeColor}
              strokeWidth="3"
              strokeLinecap="round"
              strokeDasharray="10 7"
              strokeDashoffset={-dotOffset}
            />
          )}

          {/* Direction arrows along route */}
          {routePath && bikePos && custPos && (() => {
            const points = [0.3, 0.55, 0.78];
            return points.map((t, i) => {
              const mx = (bikePos.x + custPos.x) / 2 + (custPos.y - bikePos.y) * 0.25;
              const my = (bikePos.y + custPos.y) / 2 - (custPos.x - bikePos.x) * 0.25;
              const bx = (1 - t) * (1 - t) * bikePos.x + 2 * (1 - t) * t * mx + t * t * custPos.x;
              const by = (1 - t) * (1 - t) * bikePos.y + 2 * (1 - t) * t * my + t * t * custPos.y;
              const dt = 0.05;
              const t2 = Math.min(t + dt, 1);
              const bx2 = (1 - t2) * (1 - t2) * bikePos.x + 2 * (1 - t2) * t2 * mx + t2 * t2 * custPos.x;
              const by2 = (1 - t2) * (1 - t2) * bikePos.y + 2 * (1 - t2) * t2 * my + t2 * t2 * custPos.y;
              const angle = (Math.atan2(by2 - by, bx2 - bx) * 180) / Math.PI;
              return (
                <g key={i} transform={`translate(${bx}, ${by}) rotate(${angle})`}>
                  <polygon
                    points="0,-4 7,0 0,4"
                    fill={routeColor}
                    fillOpacity="0.7"
                  />
                </g>
              );
            });
          })()}

          {/* Customer destination */}
          {custPos && (() => {
            const cx = onlyCustomer ? W / 2 : custPos.x;
            const cy = onlyCustomer ? H / 2 : custPos.y;
            return (
              <g>
                {/* Glow pulse */}
                <circle cx={cx} cy={cy} r="26" fill="url(#custGlow2)"
                  style={{ transformOrigin: `${cx}px ${cy}px`, animation: 'custPing 2s ease-in-out infinite' }} />
                {/* Drop shadow */}
                <circle cx={cx} cy={cy + 1} r="15" fill="#00000020" />
                {/* Pin background */}
                <circle cx={cx} cy={cy} r="15" fill="#16a34a" filter="url(#shadow2)" />
                {/* Inner white */}
                <circle cx={cx} cy={cy} r="10" fill="white" />
                {/* Home icon */}
                <text x={cx} y={cy + 4.5} textAnchor="middle" fontSize="12" fill="#16a34a" fontFamily="system-ui">
                  &#xf015;
                </text>
                {/* Label pill */}
                <rect x={cx - 30} y={cy + 18} width="60" height="16" rx="8" fill="#16a34a" />
                <text x={cx} y={cy + 30} textAnchor="middle" fontSize="7" fontWeight="bold" fill="white" fontFamily="system-ui, -apple-system, sans-serif" letterSpacing="0.5">
                  DESTINATION
                </text>
              </g>
            );
          })()}

          {/* Delivery bike — only when live */}
          {bikePos && locationStatus === 'tracking' && (
            <g style={{ transformOrigin: `${bikePos.x}px ${bikePos.y}px` }}>
              {/* Pulse rings */}
              {pulsing && (
                <circle cx={bikePos.x} cy={bikePos.y} r="20" fill="none" stroke="#f97316" strokeWidth="2.5"
                  style={{
                    transformOrigin: `${bikePos.x}px ${bikePos.y}px`,
                    animation: 'pulseOut 0.9s ease-out forwards',
                  }}
                />
              )}
              <circle cx={bikePos.x} cy={bikePos.y} r="18" fill="url(#bikeGlow2)" />
              {/* Direction indicator */}
              <polygon
                points={`${bikePos.x},${bikePos.y - 20} ${bikePos.x - 4},${bikePos.y - 14} ${bikePos.x + 4},${bikePos.y - 14}`}
                fill="#f97316"
                transform={`rotate(${bearing}, ${bikePos.x}, ${bikePos.y})`}
                style={{ transformOrigin: `${bikePos.x}px ${bikePos.y}px` }}
              />
              {/* Shadow */}
              <ellipse cx={bikePos.x} cy={bikePos.y + 16} rx="11" ry="3.5" fill="rgba(0,0,0,0.18)" />
              {/* Main circle */}
              <circle cx={bikePos.x} cy={bikePos.y} r="15" fill="#111827" stroke="white" strokeWidth="3" filter="url(#shadow2)" />
              {/* Bike icon */}
              <text x={bikePos.x} y={bikePos.y + 5.5} textAnchor="middle" fontSize="15" fill="white">
                🏍️
              </text>
              {/* Name label */}
              <rect x={bikePos.x - 34} y={bikePos.y - 38} width="68" height="16" rx="6" fill="#111827" fillOpacity="0.92" />
              <text x={bikePos.x} y={bikePos.y - 26} textAnchor="middle" fontSize="7.5" fontWeight="bold" fill="white" fontFamily="system-ui">
                {dbName.length > 12 ? dbName.slice(0, 12) + '…' : dbName}
              </text>
              {/* Live dot on name */}
              <circle cx={bikePos.x + 26} cy={bikePos.y - 30} r="3" fill="#22c55e"
                style={{ animation: 'blinkDot 1s ease-in-out infinite' }} />
            </g>
          )}

          {/* No GPS — centered spinner */}
          {!currentLocation && (
            <g>
              <circle cx={W / 2} cy={H - 30} r="14" fill="none" stroke="#e2e8f0" strokeWidth="3" />
              <circle cx={W / 2} cy={H - 30} r="14" fill="none" stroke="#f97316" strokeWidth="3"
                strokeDasharray="22 22" strokeLinecap="round"
                style={{ transformOrigin: `${W / 2}px ${H - 30}px`, animation: 'spinRing 1.2s linear infinite' }} />
              <text x={W / 2} y={H - 25} textAnchor="middle" fontSize="14" fill="#f97316">🏍️</text>
              <rect x={W / 2 - 38} y={H - 18} width="76" height="14" rx="7" fill="#111827" fillOpacity="0.8" />
              <text x={W / 2} y={H - 7} textAnchor="middle" fontSize="7" fontWeight="bold" fill="white" fontFamily="system-ui">
                GPS LOCATING...
              </text>
            </g>
          )}

          {/* CSS animations inline */}
          <style>{`
            @keyframes custPing {
              0%, 100% { opacity: 0.8; transform: scale(1); }
              50% { opacity: 0.2; transform: scale(1.4); }
            }
            @keyframes pulseOut {
              0% { transform: scale(1); opacity: 0.7; }
              100% { transform: scale(2.8); opacity: 0; }
            }
            @keyframes blinkDot {
              0%, 100% { opacity: 1; }
              50% { opacity: 0.2; }
            }
            @keyframes spinRing {
              to { transform: rotate(360deg); }
            }
          `}</style>
        </svg>

        {/* Distance badge top-right overlay */}
        {distLabel && (
          <div className={`absolute top-3 right-3 px-3 py-2 rounded-xl text-xs font-bold border flex items-center gap-1.5 shadow-sm ${
            isClose ? 'bg-green-100 text-green-700 border-green-300' :
            isNear ? 'bg-amber-100 text-amber-700 border-amber-300' :
            'bg-white text-gray-700 border-gray-200'
          }`}>
            <i className={`${isClose ? 'ri-checkbox-circle-fill text-green-600' : 'ri-e-bike-2-line'} text-sm`}></i>
            {distLabel}
          </div>
        )}

        {/* GPS error retry button */}
        {locationStatus === 'error' && (
          <div className="absolute bottom-3 left-3 flex flex-col gap-1.5">
            <button
              onClick={() => {
                startLocationTracking(activeOrderId);
              }}
              className="px-3 py-2 rounded-xl bg-red-500 text-white text-xs font-bold flex items-center gap-1.5 cursor-pointer whitespace-nowrap shadow"
            >
              <i className="ri-refresh-line animate-spin"></i>Retry GPS
            </button>
            <button
              onClick={() => {
                const isAndroid = /Android/i.test(navigator.userAgent);
                if (isAndroid) {
                  window.open('intent:#Intent;action=android.settings.LOCATION_SOURCE_SETTINGS;end', '_blank');
                } else {
                  showToast('Phone Settings → Privacy → Location Services → ON', 'info');
                }
              }}
              className="px-3 py-1.5 rounded-xl bg-white border border-red-200 text-red-600 text-[10px] font-bold flex items-center gap-1 cursor-pointer whitespace-nowrap"
            >
              <i className="ri-settings-3-line"></i>Open Settings
            </button>
          </div>
        )}
      </div>

      {/* Info bar */}
      <div className={`px-4 py-3 border-t flex items-center justify-between ${
        locationStatus === 'tracking' ? 'bg-white' : 'bg-amber-50 border-amber-100'
      }`}>
        <div className="flex items-center gap-2.5">
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${
            locationStatus === 'tracking' ? 'bg-gray-900' : 'bg-amber-100'
          }`}>
            <i className={`ri-e-bike-2-fill text-sm ${locationStatus === 'tracking' ? 'text-white' : 'text-amber-600'}`}></i>
          </div>
          <div>
            <p className={`font-bold text-xs leading-none ${locationStatus === 'tracking' ? 'text-gray-900' : 'text-amber-800'}`}>
              {locationStatus === 'tracking' ? dbName : 'GPS ON karo'}
            </p>
            {locationStatus === 'tracking' ? (
              <div className="flex items-center gap-1 mt-0.5">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse inline-block"></span>
                <p className="text-green-600 text-[10px] font-semibold">LIVE TRACKING</p>
              </div>
            ) : (
              <p className="text-amber-600 text-[10px] mt-0.5">Location permission chahiye</p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2.5 text-right">
          {distLabel && (
            <div>
              <p className={`text-sm font-bold leading-none ${isClose ? 'text-green-600' : isNear ? 'text-amber-600' : 'text-gray-800'}`}>
                {distLabel}
              </p>
              <p className="text-gray-400 text-[10px] mt-0.5">Door</p>
            </div>
          )}
          {etaLabel && (
            <div className={`px-2.5 py-1.5 rounded-xl text-xs font-bold border ${
              isClose ? 'bg-green-100 text-green-700 border-green-200' :
              isNear ? 'bg-amber-100 text-amber-700 border-amber-200' :
              'bg-gray-100 text-gray-700 border-gray-200'
            }`}>
              <div className="flex items-center gap-1">
                <i className="ri-time-line text-xs"></i>
                {etaLabel}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Address row */}
      {customerAddress && (
        <div className="px-4 py-2.5 bg-gray-50 border-t border-gray-100 flex items-start gap-2">
          <i className="ri-map-pin-2-fill text-green-500 text-xs mt-0.5 shrink-0"></i>
          <p className="text-gray-500 text-xs leading-relaxed line-clamp-2">{customerAddress}</p>
          {customerLat && customerLng && currentLocation && (
            <a
              href={`https://www.google.com/maps/dir/?api=1&origin=${currentLocation.lat},${currentLocation.lng}&destination=${customerLat},${customerLng}&travelmode=driving`}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-auto shrink-0 flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-gray-900 text-white text-[10px] font-bold cursor-pointer whitespace-nowrap"
            >
              <i className="ri-navigation-fill text-xs"></i>Navigate
            </a>
          )}
        </div>
      )}
    </div>
  );
}
